<template>
  <div class="laboratory-default-value-settings">
    <div class="title">
      検査機器連携/検査項目の単位、基準値、説明文のデフォルト値の設定
    </div>
    <div class="content">
      <base-select-box
        data-test="base-select-box"
        class="select-box"
        :is-value-string="true"
        :selectData="masterLaboratoryInstrumentsSelectData"
        :value="selectedMasterLaboratoryInstrumentCode"
        @input="changeInstrument"
      />
      <validation-observer class="observer" tag="div" v-slot="{ invalid }">
        <div class="row">
          <radio-button-form
            data-test="radio-button-form"
            v-bind="flgData"
            v-model="
              laboratoryDefaultValueSetting.useInstrumentUnitReferenceFlg
            "
            :contentStyles="{ width: 'auto' }"
          />
        </div>
        <div class="header">
          <div
            class="header-item name"
            :style="itemNameStyles"
            data-test="header-item name"
          >
            検査項目名
          </div>
          <div class="header-item unit">単位</div>
          <div
            v-if="isInstrumentWithMonthAgeDependentItem"
            class="header-item age"
            data-test="header-item age"
          >
            月齢
          </div>
          <div class="header-item range">基準値（イヌ）</div>
          <div class="header-item range">基準値（ネコ）</div>
          <div class="header-item description">説明文</div>
        </div>
        <div class="table">
          <laboratory-item-input-form
            data-test="laboratory-item-input-form"
            v-for="(laboratoryItem, index) in laboratoryItems"
            :key="laboratoryItem.id"
            :index="index"
            :laboratoryItem="laboratoryItem"
            :laboratoryReferenceRangesByLaboratoryItemCode="
              getLaboratoryReferenceRangesByLaboratoryItemCode(laboratoryItem)
            "
            :isInstrumentWithMonthAgeDependentItem="
              isInstrumentWithMonthAgeDependentItem
            "
            :itemNameStyles="itemNameStyles"
            @input-laboratory-item-value="inputLaboratoryItemValue"
            @input-laboratory-reference-range-value="
              inputLaboratoryReferenceRangeValue
            "
          />
        </div>
        <div class="decision-button">
          <base-button-medium-orange
            data-test="decision-button"
            v-if="lookOnlyFlg === 0"
            :disabled="awaiting || invalid"
            @click="updateLaboratoryData"
            >登録</base-button-medium-orange
          >
        </div>
      </validation-observer>
      <announce-popup
        v-if="popup.opened"
        :type="popup.type"
        :title="popup.title"
        :buttons="popup.buttons"
        @cancel="popup.cancel"
        @close="popup.close"
        @decision="popup.decide"
        >{{ popup.message }}</announce-popup
      >
    </div>
    <unsaved-leave-popup />
  </div>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonMediumOrange from '@/components/parts/atoms/BaseButtonMediumOrange'
import BaseSelectBox from '@/components/parts/atoms/BaseSelectBox'
import RadioButtonForm from '@/components/parts/molecules/RadioButtonForm.vue'
import CheckInputDifference from '@/components/mixins/CheckInputDifference'
import UnsavedLeavePopup from '@/components/popups/UnsavedLeavePopup'
import LaboratoryItemInputForm from '@/components/parts/organisms/LaboratoryItemInputForm'
import { ValidationObserver } from 'vee-validate'
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'

export default {
  name: 'LaboratoryDefaultValueSettings',

  components: {
    AnnouncePopup,
    BaseButtonMediumOrange,
    BaseSelectBox,
    RadioButtonForm,
    LaboratoryItemInputForm,
    UnsavedLeavePopup,
    ValidationObserver
  },

  mixins: [CheckInputDifference],

  data() {
    return {
      flgData: {
        title: '未取込検査結果の単位・基準値',
        radioButtonData: [
          {
            id: 1,
            eachValue: 1,
            labelName: '検査機器から送られてきた単位・基準値を表示'
          },
          {
            id: 2,
            eachValue: 0,
            labelName: 'この画面で設定した単位・基準値を表示'
          }
        ]
      },
      laboratoryDefaultValueSetting: {},
      laboratoryItems: [],
      laboratoryReferenceRanges: [],
      selectedMasterLaboratoryInstrumentCode: '030',
      previousSelectedMasterLaboratoryInstrumentCode: '',
      awaiting: false,
      popup: {
        buttons: ['閉じる'],
        cancel: () => {},
        close: this.closePopup,
        decide: () => {},
        message: '',
        opened: false,
        title: '',
        type: ''
      }
    }
  },

  computed: {
    ...mapGetters({
      lookOnlyFlg: 'auth/lookOnlyFlg',
      masterLaboratoryInstruments: 'master/getMasterLaboratoryInstruments',
      getLaboratoryDefaultValueSettings:
        'laboratoryDefaultValueSettings/getData',
      getLaboratoryItems: 'laboratoryItems/getData',
      getLaboratoryReferenceRanges: 'laboratoryReferenceRanges/getData'
    }),
    masterLaboratoryInstrumentsSelectData() {
      return this.masterLaboratoryInstruments
        .filter(v => v.code !== '036')
        .map(v => ({ id: v.code, name: v.name }))
    },
    isInstrumentWithMonthAgeDependentItem() {
      const monthAgeDependentInstrumentCodes = ['031', '003', '015'] //月齢によって基準値が変わる検査項目が１つでもある検査機器のコード
      return monthAgeDependentInstrumentCodes.includes(
        this.selectedMasterLaboratoryInstrumentCode
      )
    },
    itemNameStyles() {
      return this.selectedMasterLaboratoryInstrumentCode === '030'
        ? { width: '190px' } // 富士ドライケムAU10Vでは検査項目名が長いものがあるため、幅を広げる
        : { width: '100px' }
    }
  },

  created() {
    this.setData()
  },

  methods: {
    changeInstrument(value) {
      if (this.mixinDiffFlg) {
        this.previousSelectedMasterLaboratoryInstrumentCode = this.selectedMasterLaboratoryInstrumentCode
        this.selectedMasterLaboratoryInstrumentCode = value
        this.popup = {
          buttons: ['キャンセル', '検査機器を変更'],
          cancel: () => {
            this.selectedMasterLaboratoryInstrumentCode = this.previousSelectedMasterLaboratoryInstrumentCode
            this.popup.opened = false
          },
          decide: () => {
            this.setData()
            this.popup.opened = false
          },
          close: () => {},
          message:
            '入力内容を保存せずに別の検査機器の設定画面に変えしようとしています。\n別の検査機器の設定画面に変えますか？',
          opened: true,
          title: '確認',
          type: 'alert'
        }
      } else {
        this.selectedMasterLaboratoryInstrumentCode = value
        this.setData()
      }
    },
    inputLaboratoryItemValue({ index, key, value }) {
      this.laboratoryItems[index][key] = value
    },
    inputLaboratoryReferenceRangeValue({
      laboratoryReferenceRangeId,
      key,
      value
    }) {
      const targetIndex = this.laboratoryReferenceRanges.findIndex(
        v => v.id === laboratoryReferenceRangeId
      )
      const targetLaboratoryReferenceRange = this.laboratoryReferenceRanges[
        targetIndex
      ]
      targetLaboratoryReferenceRange[key] = value
      this.$set(
        this.laboratoryReferenceRanges,
        targetIndex,
        targetLaboratoryReferenceRange
      )
    },
    sortLaboratoryItems(laboratoryItems) {
      if (laboratoryItems[0].masterLaboratoryInstrumentCode === '030') {
        const order = [
          'L002220',
          'L002221',
          'L002222',
          'L002225',
          'L002218',
          'L002224',
          'L002219',
          'L002227',
          'L002228',
          'L002229'
        ] //表示する順番
        return laboratoryItems.sort((a, b) => {
          return (
            order.indexOf(a.masterLaboratoryItemCode) -
            order.indexOf(b.masterLaboratoryItemCode)
          )
        })
      } else {
        return laboratoryItems.sort((a, b) => {
          return a.id - b.id
        })
      }
    },
    setData() {
      const laboratoryDefaultValueSetting = this.getLaboratoryDefaultValueSettings.find(
        v =>
          v.masterLaboratoryInstrumentCode ===
          this.selectedMasterLaboratoryInstrumentCode
      )
      this.laboratoryDefaultValueSetting = cloneDeep(
        laboratoryDefaultValueSetting
      )
      const laboratoryItems = this.sortLaboratoryItems(
        this.getLaboratoryItems.filter(v => {
          return (
            v.masterLaboratoryInstrumentCode ===
            this.selectedMasterLaboratoryInstrumentCode
          )
        })
      )
      this.laboratoryItems = cloneDeep(laboratoryItems)
      const laboratoryReferenceRanges = this.getLaboratoryReferenceRanges.filter(
        v => {
          return (
            v.masterLaboratoryInstrumentCode ===
            this.selectedMasterLaboratoryInstrumentCode
          )
        }
      )
      this.laboratoryReferenceRanges = cloneDeep(laboratoryReferenceRanges)
      this.mixinInputData = {
        laboratoryDefaultValueSetting: this.laboratoryDefaultValueSetting,
        laboratoryItems: this.laboratoryItems,
        laboratoryReferenceRanges: this.laboratoryReferenceRanges
      }
      this.mixinSetInitialData()
    },
    sortLaboratoryReferenceRanges(laboratoryReferenceRanges) {
      return laboratoryReferenceRanges.sort((a, b) => {
        if (a.minMonthAge !== b.minMonthAge) {
          return a.minMonthAge < b.minMonthAge ? -1 : 1
        } else {
          return (
            Number(a.masterLaboratorySpeciesCode) -
            Number(b.masterLaboratorySpeciesCode)
          )
        }
      })
    },
    getLaboratoryReferenceRangesByLaboratoryItemCode(laboratoryItem) {
      const laboratoryReferenceRangesByLaboratoryItemCode = this.laboratoryReferenceRanges.filter(
        laboratoryReferenceRange => {
          return (
            laboratoryItem.masterLaboratoryItemCode ===
            laboratoryReferenceRange.masterLaboratoryItemCode
          )
        }
      )
      return this.sortLaboratoryReferenceRanges(
        laboratoryReferenceRangesByLaboratoryItemCode
      )
    },
    async updateLaboratoryData() {
      this.awaiting = true
      const result = await this.$store.dispatch(
        'laboratoryDefaultValueSettings/update',
        {
          laboratoryDefaultValueSetting: this.laboratoryDefaultValueSetting,
          laboratoryItems: this.laboratoryItems,
          laboratoryReferenceRanges: this.laboratoryReferenceRanges
        }
      )
      if (result === 'success') {
        this.popup.type = 'success'
        this.popup.title = '完了'
        this.popup.message = '編集しました'
        this.popup.close = this.closePopup
        this.setData()
      } else {
        this.popup.type = 'failure'
        this.popup.title = '失敗'
        this.popup.message = '編集に失敗しました'
        this.popup.close = this.closePopup
      }
      this.popup.buttons = ['閉じる']
      this.popup.opened = true
      this.awaiting = false
    },
    closePopup() {
      this.popup.opened = false
    }
  }
}
</script>

<style lang="scss" scoped>
.laboratory-default-value-settings {
  width: 100%;
  min-width: 680px;
  text-align: left;
  > .title {
    padding-bottom: 20px;
    font-size: 20px;
    font-weight: bold;
    border-bottom: 1px solid #{$light-grey};
  }
  > .content {
    display: inline-block;
    > .select-box {
      margin-top: 20px;
    }
    > .observer {
      margin-top: 20px;
      margin-left: 10px;
      > .header {
        display: flex;
        margin-top: 25px;
        padding-bottom: 5px;
        > .header-item {
          font-weight: bold;
          font-size: 15px;
          font-feature-settings: 'palt';
          &.unit {
            margin-left: 20px;
            width: 140px;
            text-align: center;
          }
          &.age {
            margin-left: 20px;
            width: 120px;
            text-align: center;
          }
          &.range {
            margin-left: 20px;
            width: 200px;
            text-align: center;
          }
          &.description {
            margin-left: 27px;
            width: 384px;
          }
        }
      }
      > .table {
        overflow: auto;
        max-height: 700px;
        padding-right: 20px;
      }
      > .decision-button {
        margin: 60px 0px 60px 500px;
        height: 100px;
      }
    }
  }
}
</style>
